import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import { toast } from "sonner";
import { Clock, Banknote, ShieldAlert, Truck, PackageCheck, Printer, Layers } from "lucide-react";
import { CARRIERS, trackingUrl } from "@/lib/taxonomy";

const PAYOUT = {
  held: { label: "Payout held", color: "#EA580C", Icon: Clock },
  released: { label: "Payout released", color: "#16A34A", Icon: Banknote },
  refunded: { label: "Refunded to buyer", color: "#E11D48", Icon: ShieldAlert },
};

const FULFIL = {
  awaiting_shipment: { label: "Awaiting shipment", color: "#EA580C" },
  shipped: { label: "In transit", color: "#2563EB" },
  delivered: { label: "Delivered", color: "#16A34A" },
};

export default function SellerSales() {
  const [sales, setSales] = useState([]);
  const [replyFor, setReplyFor] = useState(null);
  const [message, setMessage] = useState("");
  const [shipFor, setShipFor] = useState(null);
  const [ship, setShip] = useState({ carrier: CARRIERS[0], tracking_number: "", inspected: false });
  const [labelFor, setLabelFor] = useState(null);
  const [shipFrom, setShipFrom] = useState({ name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US", phone: "", email: "" });
  const [parcel, setParcel] = useState({ weight_lb: "1", length_in: "12", width_in: "10", height_in: "3" });
  const [labelInspected, setLabelInspected] = useState(false);
  const [buying, setBuying] = useState(false);

  const load = () => api.get("/seller/sales").then((r) => setSales(r.data));
  useEffect(() => {
    load();
    api.get("/seller/ship-from").then((r) => { if (r.data && r.data.street1) setShipFrom((v) => ({ ...v, ...r.data })); }).catch(() => {});
  }, []);

  // Group sales into per-seller bundles (one label / one shipment covers every item).
  const bundles = React.useMemo(() => {
    const m = {};
    sales.forEach((s) => {
      const key = s.bundle_id || s.id;
      (m[key] || (m[key] = [])).push(s);
    });
    return Object.entries(m).map(([key, its]) => {
      const primary = its.find((x) => x.bundle_primary) || its[0];
      return { key, primary, items: its };
    }).sort((a, b) => new Date(b.primary.paid_at) - new Date(a.primary.paid_at));
  }, [sales]);

  const buyLabel = async (oid) => {
    if (!labelInspected) { toast.error("Confirm you inspected the item first"); return; }
    setBuying(true);
    try {
      const { data } = await api.post(`/seller/sales/${oid}/label`, {
        ship_from: shipFrom,
        weight_lb: Number(parcel.weight_lb) || 1, length_in: Number(parcel.length_in) || 12,
        width_in: Number(parcel.width_in) || 10, height_in: Number(parcel.height_in) || 3,
        save_address: true, inspected: true,
      });
      toast.success(`Label ready — ${data.carrier} ${data.tracking_number}${data.bundle_items > 1 ? ` (covers ${data.bundle_items} items)` : ""}`);
      if (data.cost_alert) {
        toast.warning(`Heads up: this ${data.carrier} label cost $${data.cost_alert.label_cost.toFixed(2)} but the buyer paid $${data.cost_alert.buyer_paid.toFixed(2)} — a $${data.cost_alert.shortfall.toFixed(2)} shortfall.`, { duration: 9000 });
      }
      setLabelFor(null); setLabelInspected(false); load();
      if (data.label_url) window.open(data.label_url, "_blank");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBuying(false); }
  };

  const respond = async (oid) => {
    if (!message.trim()) { toast.error("Write your response first"); return; }
    try {
      await api.post(`/seller/sales/${oid}/respond`, { message });
      toast.success("Response sent — the claim is being re-reviewed");
      setReplyFor(null); setMessage(""); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markShipped = async (oid) => {
    if (!ship.inspected) { toast.error("Confirm you inspected the item before shipping"); return; }
    try {
      await api.post(`/seller/sales/${oid}/ship`, ship);
      toast.success("Marked shipped — the buyer can follow the parcel now");
      setShipFor(null); setShip({ carrier: CARRIERS[0], tracking_number: "", inspected: false }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markDelivered = async (oid) => {
    try {
      await api.post(`/seller/sales/${oid}/delivered`);
      toast.success("Delivery confirmed — the 3-day guarantee clock has started");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (sales.length === 0) {
    return (
      <div className="hairline p-10 mt-6 text-center" data-testid="sales-empty">
        <div className="label-eyebrow">No sales yet</div>
        <p className="mt-2 text-sm text-neutral-500">
          When you sell, mark the parcel shipped here. The payment is released 3 days after delivery.
        </p>
      </div>
    );
  }

  return (
    <ul className="mt-6 divide-y divide-black/10" data-testid="sales-list">
      {bundles.map(({ key, primary: s, items }) => {
        const isBundle = items.length > 1;
        const p = PAYOUT[s.payout_status] || PAYOUT.held;
        const fs = s.fulfilment_status || (s.guarantee_expires_at ? "delivered" : "awaiting_shipment");
        const f = FULFIL[fs];
        const track = s.tracking_url || trackingUrl(s.carrier, s.tracking_number);
        const hasAddress = !!(s.ship_to && s.ship_to.street1);
        const bundleAmount = items.reduce((a, x) => a + Number(x.amount || 0), 0);
        const bundleNet = items.reduce((a, x) => a + Number(x.seller_net || 0), 0);
        const claims = items.filter((x) => x.claim);
        return (
          <li key={key} className="py-5" data-testid={`sale-row-${s.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 hairline overflow-hidden shrink-0">
                <ImageThumb path={s.image_path} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                {isBundle ? (
                  <div className="font-display flex items-center gap-1.5" data-testid={`bundle-title-${s.id}`}>
                    <Layers size={14} style={{ color: "#1E3A2B" }} /> Bundle · {items.length} items
                  </div>
                ) : (
                  <div className="font-display truncate">{s.title}</div>
                )}
                {isBundle && (
                  <div className="text-xs text-neutral-600 mt-0.5 truncate">
                    {items.map((x) => x.title).join(" · ")}
                  </div>
                )}
                <div className="text-xs text-neutral-500 mt-1">
                  Sold {new Date(s.paid_at).toLocaleDateString()}
                  {s.delivered_at ? ` · delivered ${new Date(s.delivered_at).toLocaleDateString()}` : ""}
                  {s.guarantee_expires_at ? ` · cover ends ${new Date(s.guarantee_expires_at).toLocaleDateString()}` : " · cover starts on delivery"}
                  {isBundle ? " · one label for the bundle" : ""}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[0.62rem] uppercase tracking-[0.16em]">
                  <span style={{ color: f.color }} data-testid={`fulfilment-${s.id}`}>{f.label}</span>
                  {s.buyer_shipping_service && !s.tracking_number && (
                    <span className="text-neutral-600" data-testid={`sale-service-${s.id}`}>
                      Ship via {s.buyer_shipping_carrier ? `${s.buyer_shipping_carrier} ` : ""}{s.buyer_shipping_service}
                    </span>
                  )}
                  {s.tracking_number && (
                    track ? (
                      <a href={track} target="_blank" rel="noreferrer" className="underline" style={{ color: "#2563EB" }}>
                        {s.carrier} {s.tracking_number}
                      </a>
                    ) : <span className="text-neutral-600">{s.carrier} {s.tracking_number}</span>
                  )}
                  {s.label_url && (
                    <a href={s.label_url} target="_blank" rel="noreferrer" data-testid={`label-link-${s.id}`}
                      className="underline inline-flex items-center gap-1" style={{ color: "#1E3A2B" }}>
                      <Printer size={11} /> Print label
                    </a>
                  )}
                  <span className="inline-flex items-center gap-1.5" style={{ color: p.color }} data-testid={`payout-${s.id}`}>
                    <p.Icon size={12} /> {p.label}
                  </span>
                </div>
                {s.shipping_cost_alert && (
                  <div className="mt-1.5 text-[0.62rem]" style={{ color: "#B91C1C" }} data-testid={`ship-alert-${s.id}`}>
                    Shipping shortfall ${Number(s.shipping_cost_alert.shortfall).toFixed(2)} — label ${Number(s.shipping_cost_alert.label_cost).toFixed(2)} vs buyer paid ${Number(s.shipping_cost_alert.buyer_paid).toFixed(2)}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-display">${bundleAmount.toFixed(2)}</div>
                <div className="text-[0.6rem] uppercase tracking-[0.14em] text-neutral-500 mt-0.5" data-testid={`net-${s.id}`}>
                  net ${bundleNet.toFixed(2)}
                </div>
                {fs !== "delivered" && (
                  <div className="mt-2 flex flex-col gap-2 items-end">
                    {fs === "awaiting_shipment" ? (
                      hasAddress ? (
                        <button className="btn-klein text-[0.62rem]" data-testid={`label-btn-${s.id}`}
                          onClick={() => setLabelFor(labelFor === s.id ? null : s.id)}>
                          <Printer size={12} /> Buy &amp; print {isBundle ? "one label" : "label"}
                        </button>
                      ) : (
                        <button className="btn-klein text-[0.62rem]" data-testid={`ship-btn-${s.id}`}
                          onClick={() => setShipFor(shipFor === s.id ? null : s.id)}>
                          <Truck size={12} /> Mark shipped
                        </button>
                      )
                    ) : (
                      <button className="btn-outline text-[0.62rem]" data-testid={`delivered-btn-${s.id}`}
                        onClick={() => markDelivered(s.id)}>
                        <PackageCheck size={12} /> Mark delivered
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {shipFor === s.id && (
              <div className="mt-3 ml-[4.5rem] hairline p-4" data-testid={`ship-panel-${s.id}`}>
                <div className="label-eyebrow">Shipment details{isBundle ? ` · ${items.length} items in one parcel` : ""}</div>
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <select className="hairline p-2 text-sm bg-white" data-testid={`ship-carrier-${s.id}`}
                    value={ship.carrier} onChange={(e) => setShip((v) => ({ ...v, carrier: e.target.value }))}>
                    {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input className="hairline p-2 text-sm flex-1 min-w-[180px]" placeholder="Tracking number (optional)"
                    data-testid={`ship-tracking-${s.id}`} value={ship.tracking_number}
                    onChange={(e) => setShip((v) => ({ ...v, tracking_number: e.target.value }))} />
                  <button className="btn-klein text-[0.62rem]" onClick={() => markShipped(s.id)}
                    disabled={!ship.inspected} data-testid={`ship-confirm-${s.id}`}>Confirm shipped</button>
                  <button className="chip" onClick={() => setShipFor(null)}>Cancel</button>
                </div>
                <label className="mt-3 flex items-center gap-2.5 text-sm cursor-pointer select-none">
                  <input type="checkbox" className="w-4 h-4 accent-[#1E3A2B]" checked={ship.inspected}
                    data-testid={`ship-inspected-${s.id}`}
                    onChange={(e) => setShip((v) => ({ ...v, inspected: e.target.checked }))} />
                  <span>I inspected {isBundle ? "these items" : "this item"} before shipping — {isBundle ? "they match" : "it matches"} the listing, grade and measurements.</span>
                </label>
                <p className="mt-2 text-xs text-neutral-500">
                  The buyer can confirm arrival themselves; otherwise we mark it delivered 7 days after shipping.
                </p>
              </div>
            )}

            {labelFor === s.id && (
              <div className="mt-3 ml-[4.5rem] hairline p-4" data-testid={`label-panel-${s.id}`} style={{ borderColor: "#1E3A2B" }}>
                <div className="label-eyebrow">
                  Prepaid {isBundle ? "bundle " : ""}label — ships to {s.ship_to?.city}{s.ship_to?.state ? `, ${s.ship_to.state}` : ""}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  {isBundle
                    ? `One label covers all ${items.length} items to this buyer. Free to you — covered by the shipping the buyer paid.`
                    : "Free to you — covered by the shipping the buyer paid. We'll fetch a rate and issue a printable label + tracking."}
                </p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  <input className="hairline p-2 text-sm" placeholder="Your name" data-testid={`lf-name-${s.id}`}
                    value={shipFrom.name} onChange={(e) => setShipFrom((v) => ({ ...v, name: e.target.value }))} />
                  <input className="hairline p-2 text-sm" placeholder="Phone" data-testid={`lf-phone-${s.id}`}
                    value={shipFrom.phone} onChange={(e) => setShipFrom((v) => ({ ...v, phone: e.target.value }))} />
                  <input className="hairline p-2 text-sm sm:col-span-2" placeholder="Email (for the carrier)" data-testid={`lf-email-${s.id}`}
                    value={shipFrom.email} onChange={(e) => setShipFrom((v) => ({ ...v, email: e.target.value }))} />
                  <input className="hairline p-2 text-sm sm:col-span-2" placeholder="Street address" data-testid={`lf-street1-${s.id}`}
                    value={shipFrom.street1} onChange={(e) => setShipFrom((v) => ({ ...v, street1: e.target.value }))} />
                  <input className="hairline p-2 text-sm" placeholder="City" data-testid={`lf-city-${s.id}`}
                    value={shipFrom.city} onChange={(e) => setShipFrom((v) => ({ ...v, city: e.target.value }))} />
                  <div className="flex gap-2">
                    <input className="hairline p-2 text-sm w-20" placeholder="State" data-testid={`lf-state-${s.id}`}
                      value={shipFrom.state} onChange={(e) => setShipFrom((v) => ({ ...v, state: e.target.value }))} />
                    <input className="hairline p-2 text-sm flex-1 min-w-0" placeholder="ZIP" data-testid={`lf-zip-${s.id}`}
                      value={shipFrom.zip} onChange={(e) => setShipFrom((v) => ({ ...v, zip: e.target.value }))} />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-neutral-500">Parcel</span>
                  <input className="hairline p-2 text-sm w-16" title="Weight (lb)" data-testid={`lf-weight-${s.id}`}
                    value={parcel.weight_lb} onChange={(e) => setParcel((v) => ({ ...v, weight_lb: e.target.value }))} />
                  <span className="text-xs text-neutral-400">lb ·</span>
                  <input className="hairline p-2 text-sm w-14" title="Length (in)"
                    value={parcel.length_in} onChange={(e) => setParcel((v) => ({ ...v, length_in: e.target.value }))} />
                  <input className="hairline p-2 text-sm w-14" title="Width (in)"
                    value={parcel.width_in} onChange={(e) => setParcel((v) => ({ ...v, width_in: e.target.value }))} />
                  <input className="hairline p-2 text-sm w-14" title="Height (in)"
                    value={parcel.height_in} onChange={(e) => setParcel((v) => ({ ...v, height_in: e.target.value }))} />
                  <span className="text-xs text-neutral-400">in · L×W×H</span>
                </div>
                <label className="mt-3 flex items-center gap-2.5 text-sm cursor-pointer select-none">
                  <input type="checkbox" className="w-4 h-4 accent-[#1E3A2B]" checked={labelInspected}
                    data-testid={`lf-inspected-${s.id}`} onChange={(e) => setLabelInspected(e.target.checked)} />
                  <span>I inspected {isBundle ? "these items" : "this item"} before shipping — {isBundle ? "they match" : "it matches"} the listing, grade and measurements.</span>
                </label>
                <div className="mt-3 flex gap-2">
                  <button className="btn-klein text-[0.62rem]" onClick={() => buyLabel(s.id)}
                    disabled={buying || !labelInspected} data-testid={`lf-buy-${s.id}`}>
                    {buying ? "Buying label…" : `Buy & print ${isBundle ? "one label" : "label"}`}
                  </button>
                  <button className="chip" onClick={() => setLabelFor(null)}>Cancel</button>
                </div>
              </div>
            )}

            {claims.map((ci) => (
              <div key={ci.id} className="mt-3 ml-[4.5rem] hairline p-4" data-testid={`sale-claim-${ci.id}`} style={{ borderColor: "#EA580C" }}>
                <div className="label-eyebrow" style={{ color: "#EA580C" }}>
                  Buyer authenticity claim{isBundle ? ` · ${ci.title}` : ""} · {ci.claim.status}
                </div>
                <div className="mt-2 text-sm">{ci.claim.reason}</div>
                {ci.claim.note && <p className="mt-1 text-xs text-neutral-500">“{ci.claim.note}”</p>}
                {ci.claim.ai?.reasoning && <p className="mt-2 text-xs text-neutral-600">AI review: {ci.claim.ai.reasoning}</p>}
                {ci.claim.ai?.decision === "inconclusive" && (
                  <p className="mt-1 text-xs text-neutral-500" data-testid={`sale-inconclusive-${ci.id}`}>
                    Evidence was inconclusive — the buyer was refunded but no strike was applied to your account.
                  </p>
                )}
                {ci.claim.seller_response ? (
                  <p className="mt-2 text-xs text-neutral-500">Your response: “{ci.claim.seller_response}”</p>
                ) : replyFor === ci.id ? (
                  <div className="mt-3">
                    <textarea rows={3} className="w-full hairline p-2 text-sm" data-testid={`sale-reply-input-${ci.id}`}
                      placeholder="Explain the provenance, receipts or date codes that prove it's genuine"
                      value={message} onChange={(e) => setMessage(e.target.value)} />
                    <div className="mt-2 flex gap-2">
                      <button className="btn-klein text-[0.62rem]" onClick={() => respond(ci.id)} data-testid={`sale-reply-send-${ci.id}`}>Send response</button>
                      <button className="chip" onClick={() => { setReplyFor(null); setMessage(""); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-outline text-[0.62rem] mt-3" onClick={() => setReplyFor(ci.id)} data-testid={`sale-reply-${ci.id}`}>
                    Defend this sale
                  </button>
                )}
              </div>
            ))}
          </li>
        );
      })}
    </ul>
  );
}
